import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Heart, ArrowRight, Loader2, Mail, Crown, User, Shield, Compass, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_INFO = {
  identite: {
    fr: { name: "Identité", icon: User, color: "#8B5A6B", description: "Ta reconstruction identitaire" },
    en: { name: "Identity", icon: User, color: "#8B5A6B", description: "Your identity reconstruction" }
  },
  solitude: {
    fr: { name: "Solitude", icon: Users, color: "#7A8B99", description: "Ta solitude sociale" },
    en: { name: "Loneliness", icon: Users, color: "#7A8B99", description: "Your social loneliness" }
  },
  confiance: {
    fr: { name: "Confiance", icon: Shield, color: "#D4A574", description: "Ta confiance en l'avenir" },
    en: { name: "Confidence", icon: Shield, color: "#D4A574", description: "Your confidence in the future" }
  },
  projection: {
    fr: { name: "Projection", icon: Compass, color: "#3B7A8C", description: "Ta capacité à te projeter" },
    en: { name: "Projection", icon: Compass, color: "#3B7A8C", description: "Your ability to envision the future" }
  }
};

export default function SeparationResults() {
  const { sessionId } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isFrench = i18n.language === 'fr';

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await axios.get(`${API}/api/separation/diagnostic/${sessionId}`);
        setResults(response.data);
      } catch (err) {
        console.error("Fetch error:", err);
        setError(isFrench ? "Impossible de charger les résultats" : "Unable to load results");
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [sessionId, isFrench]);

  const handleSendEmail = async () => {
    if (!results?.email) return;
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/api/separation/diagnostic/send-email`, {
        session_id: sessionId,
        email: results.email,
        language: i18n.language
      });
      setEmailSent(true);
    } catch (err) {
      console.error("Email error:", err);
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FDF8F7] to-[#F5EEED] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#8B5A6B] animate-spin mx-auto mb-4" />
          <p className="text-[#5C5C5C]">{isFrench ? "Chargement de ton analyse..." : "Loading your analysis..."}</p>
        </div>
      </div>
    );
  }

  if (error || !results) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FDF8F7] to-[#F5EEED] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={() => navigate("/")} variant="outline">
            {isFrench ? "Retour à l'accueil" : "Back to home"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF8F7] to-[#F5EEED]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#E8E4DE] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#8B5A6B]" />
            <span className="font-heading text-[#8B5A6B] font-medium">Épanoui</span>
          </Link>
          <Link to="/emergence/parcours">
            <Button variant="outline" size="sm" className="border-[#8B5A6B] text-[#8B5A6B]">
              <Crown className="w-4 h-4 mr-2" />
              Premium
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Title Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#8B5A6B]/10 text-[#8B5A6B] px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Heart className="w-4 h-4" />
            {isFrench ? "Ton analyse personnalisée" : "Your personalized analysis"}
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#2D2D2D] mb-3">
            {isFrench ? "Ce que tu traverses est cohérent" : "What you're going through makes sense"}
          </h1>
          <p className="text-[#5C5C5C] text-lg">
            {isFrench 
              ? "Voici ce qui se joue pour toi en ce moment"
              : "Here's what's happening for you right now"}
          </p>
        </div>

        {/* Overall Score */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-[#2D2D2D]">
              {isFrench ? "Score global de reconstruction" : "Overall reconstruction score"}
            </h3>
            <span className="text-3xl font-bold text-[#8B5A6B]">{results.scores?.overall || 0}%</span>
          </div>
          <Progress value={results.scores?.overall || 0} className="h-3 bg-[#E8E4DE]" />
          <p className="text-sm text-[#8A8A8A] mt-2">
            {isFrench 
              ? "Plus le score est élevé, plus tu as besoin d'accompagnement dans ce domaine"
              : "The higher the score, the more support you need in this area"}
          </p>
        </div>

        {/* Category Scores */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {Object.entries(results.scores || {}).filter(([key]) => key !== 'overall').map(([category, score]) => {
            const info = CATEGORY_INFO[category]?.[isFrench ? 'fr' : 'en'];
            if (!info) return null;
            const Icon = info.icon;
            
            return (
              <div key={category} className="bg-white rounded-xl p-5 shadow-md">
                <div className="flex items-center gap-3 mb-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${info.color}20` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: info.color }} />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#2D2D2D]">{info.name}</h4>
                    <p className="text-xs text-[#8A8A8A]">{info.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Progress value={score} className="flex-1 h-2" />
                  <span className="text-sm font-medium" style={{ color: info.color }}>{score}%</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* AI Analysis */}
        {results.analysis && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <h3 className="font-heading text-xl text-[#2D2D2D] mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5 text-[#8B5A6B]" />
              {isFrench ? "Ton analyse personnalisée" : "Your personalized analysis"}
            </h3>
            <div className="prose prose-sm max-w-none text-[#5C5C5C] leading-relaxed whitespace-pre-line">
              {results.analysis}
            </div>
          </div>
        )}

        {/* Quote */}
        <div className="bg-[#8B5A6B]/5 rounded-xl p-6 mb-8 border border-[#8B5A6B]/20">
          <p className="text-center text-[#5C5C5C] italic leading-relaxed">
            {isFrench 
              ? "« Après une séparation, on ne souffre pas seulement de la rupture. On souffre de ne plus savoir où est sa place dans un monde qui a changé. »"
              : "\"After a separation, we don't just suffer from the breakup. We suffer from no longer knowing where our place is in a world that has changed.\""}
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-4">
          {/* Email Button */}
          <Button
            onClick={handleSendEmail}
            disabled={sendingEmail || emailSent}
            variant="outline"
            className="w-full py-5 rounded-full border-[#8B5A6B] text-[#8B5A6B]"
          >
            {emailSent ? (
              <>✓ {isFrench ? "Email envoyé !" : "Email sent!"}</>
            ) : sendingEmail ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {isFrench ? "Envoi..." : "Sending..."}</>
            ) : (
              <><Mail className="w-4 h-4 mr-2" /> {isFrench ? "Recevoir par email" : "Receive by email"}</>
            )}
          </Button>

          {/* Premium CTA */}
          <Link to="/emergence/parcours" className="block">
            <Button className="w-full bg-gradient-to-r from-[#8B5A6B] to-[#A67B8B] hover:opacity-90 text-white py-6 rounded-full text-lg">
              <Crown className="w-5 h-5 mr-2" />
              {isFrench ? "Découvrir le parcours 21 jours" : "Discover the 21-day journey"}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>

          {/* Consultation CTA */}
          <Link to="/consultation/checkout" className="block">
            <Button variant="outline" className="w-full py-5 rounded-full border-2 border-[#D4A574] text-[#D4A574] hover:bg-[#D4A574]/10">
              {isFrench ? "Consultation personnalisée – 75€ / 30 min" : "Personal consultation – €75 / 30 min"}
            </Button>
          </Link>

          <p className="text-center text-sm text-[#8A8A8A]">
            {isFrench 
              ? "Un espace pour déposer ce que personne n'a le temps d'entendre."
              : "A space to share what no one has time to hear."}
          </p>
        </div>
      </main>
    </div>
  );
}
